import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Check, Copy, QrCode } from "lucide-react";
import api from "@/lib/api";

const HEARTBEAT_MS = 8000;

// Publishes the slide the leader is on so phones can follow along.
export function useLiveFollow({ sessionId, slides, font, fontSize, index, blanked, enabled }) {
  const [token, setToken] = useState(null);
  const [followers, setFollowers] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled || startedRef.current || !sessionId || !slides?.length) return;
    startedRef.current = true;
    api
      .post(`/live/sessions/${sessionId}/start`, {
        slides: slides.map((s) => ({
          category: s.category,
          title: s.title,
          lines: s.lines,
          part: s.part,
          parts_total: s.partsTotal,
        })),
        slide_index: index,
        font,
        font_size: fontSize,
      })
      .then(({ data }) => setToken(data.token))
      .catch(() => setToken(null));
  }, [enabled, sessionId, slides, font, fontSize, index]);

  useEffect(() => {
    if (!token) return;
    const send = () => api.post(`/live/sessions/${sessionId}/heartbeat`, { slide_index: index, blanked }).catch(() => {});
    send();
    const id = setInterval(send, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [token, sessionId, index, blanked]);

  useEffect(() => {
    if (!token) return;
    return () => {
      api.post(`/live/sessions/${sessionId}/stop`).catch(() => {});
    };
  }, [token, sessionId]);

  useEffect(() => {
    if (!token) return;
    const check = () =>
      api
        .get(`/live/sessions/${sessionId}/followers`)
        .then(({ data }) => setFollowers(data.count))
        .catch(() => {});
    check();
    const id = setInterval(check, 10000);
    return () => clearInterval(id);
  }, [token, sessionId]);

  return {
    followUrl: token ? `${window.location.origin}/follow/${token}` : null,
    followers,
  };
}

// Invisible hotspot on the right edge: point at it and the QR panel appears.
export function FollowQrHotspot({ url, followers = 0, onVisibilityChange }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [copied, setCopied] = useState(false);
  const hideTimer = useRef(null);
  const visible = open || pinned;

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "q" || e.key === "Q") {
        e.preventDefault();
        setPinned((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  if (!url) return null;

  const copy = async (e) => {
    e.stopPropagation();
    await navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <>
      <div
        className="absolute right-0 top-1/4 h-1/2 w-20 z-20"
        onMouseEnter={() => {
          clearTimeout(hideTimer.current);
          setOpen(true);
        }}
        onMouseLeave={() => {
          hideTimer.current = setTimeout(() => setOpen(false), 500);
        }}
        onClick={(e) => {
          e.stopPropagation();
          setPinned((p) => !p);
        }}
        title="Point here to show the follow QR code (Q)"
        data-testid="follow-qr-hotspot"
      >
        <div className={`absolute right-3 top-1/2 -translate-y-1/2 transition-opacity ${visible ? "opacity-0" : "opacity-15"}`}>
          <QrCode className="h-5 w-5 text-white" />
        </div>
      </div>

      <div
        className={`absolute right-0 top-0 h-full w-1/2 z-10 flex items-center justify-center transition-opacity duration-500 ${
          visible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onMouseEnter={() => clearTimeout(hideTimer.current)}
        onMouseLeave={() => {
          hideTimer.current = setTimeout(() => setOpen(false), 500);
        }}
        data-testid="follow-qr-panel"
      >
        <div className="flex flex-col items-center gap-4 rounded-2xl bg-white/5 backdrop-blur-sm px-8 py-7 border border-white/10">
          <p className="font-mono text-[11px] uppercase tracking-widest text-white/60">Follow on your phone</p>
          <div className="bg-white p-3 rounded-lg">
            <QRCodeSVG value={url} size={168} level="M" data-testid="follow-qr-code" />
          </div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-white/50" data-testid="follow-qr-follower-count">
            {followers === 0 ? "No one following yet" : `${followers} following`}
          </p>
          <button
            onClick={copy}
            className="flex items-center gap-2 font-mono text-[11px] text-white/50 hover:text-white/90 transition-colors"
            data-testid="follow-qr-copy-button"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {url.replace(/^https?:\/\//, "")}
          </button>
        </div>
      </div>
    </>
  );
}
